"use client"

import { Desktop, Mobile } from "@/lib/common/MediaQueries"
import { SearchHeroBannerWithText } from "@/lib/common/searchHeroBanners"
import colors from "@/lib/theme/colors"
import styled from "@emotion/styled"
import { IconButton, Typography } from "@mui/material"
import Image from "next/image"
import { useEffect, useState } from "react"
import { Arrow, ArrowButton } from "../Homepage/Hero/HeroControls"

const ROTATION_INTERVAL = 7000

// Full-bleed; height follows the design ratio but never exceeds a third of the
// viewport so the filter and results stay partially visible on load.
const Root = styled.section`
  position: relative;
  width: 100%;
  aspect-ratio: 360 / 200;
  max-height: 33.33vh;
  overflow: hidden;
  ${(props) => props.theme.breakpoints.up("md")} {
    aspect-ratio: 1280 / 200;
  }
`

const Slide = styled.div<{ $active: boolean }>`
  position: absolute;
  inset: 0;
  opacity: ${({ $active }) => ($active ? 1 : 0)};
  visibility: ${({ $active }) => ($active ? "visible" : "hidden")};
  transition:
    opacity 0.5s ease,
    visibility 0.5s;
`

// next/image `fill` needs a positioned parent, so each breakpoint box covers the slide.
const mediaSx = { position: "absolute", inset: 0 } as const

const Scrim = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.12) 0%, rgba(0, 0, 0, 0.8) 100%);
  ${(props) => props.theme.breakpoints.up("md")} {
    background: linear-gradient(180deg, rgba(0, 0, 0, 0.6) 0%, rgba(0, 0, 0, 0) 30%),
      linear-gradient(
        90deg,
        rgba(0, 0, 0, 0.15) 0%,
        rgba(0, 0, 0, 0.3) 50%,
        rgba(0, 0, 0, 0.5) 100%
      ),
      linear-gradient(90deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.8) 100%);
  }
`

// Mobile: centred in the banner, clear of the side arrows. Desktop: bottom,
// right-aligned (start in RTL) and inset past the arrows to the content width.
const Content = styled.div`
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 16px 56px;
  color: ${colors.white};
  text-align: center;
  ${(props) => props.theme.breakpoints.up("md")} {
    align-items: flex-start;
    justify-content: flex-end;
    padding: 32px 104px;
    text-align: start;
  }
`

// Theme variants carry the base size (h3 / body2); colour is overridden because
// the variants default to black. Rendered as <p> so the page H1 stays the only heading.
const Title = styled(Typography)<{ component: React.ElementType }>`
  color: ${colors.white};
  font-weight: 700;
  line-height: 28px;
  min-height: 28px;
  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 32px;
    line-height: 40px;
    min-height: unset;
  }
`

const Subtitle = styled(Typography)<{ component: React.ElementType }>`
  color: ${colors.white};
  line-height: 20px;
  min-height: 20px;
  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 18px;
    line-height: normal;
    min-height: 26px;
  }
`

// Figma lays the controls out physically (next arrow on the left), so the
// layer opts out of RTL. Pointer events stay on the buttons only.
const Controls = styled.div`
  direction: ltr;
  position: absolute;
  inset: 0;
  pointer-events: none;
`

const ArrowsGroup = styled.div`
  position: absolute;
  top: 50%;
  left: 16px;
  right: 16px;
  transform: translateY(-50%);
  display: flex;
  justify-content: space-between;
  ${(props) => props.theme.breakpoints.up("md")} {
    left: 40px;
    right: 40px;
  }
`

// 24px icon; desktop keeps a 44px hit area centred under the 40px arrow.
const PauseButton = styled(IconButton)`
  position: absolute;
  left: 16px;
  bottom: 16px;
  width: 24px;
  height: 24px;
  padding: 0;
  pointer-events: auto;
  ${(props) => props.theme.breakpoints.up("md")} {
    left: 38px;
    bottom: 16px;
    width: 44px;
    height: 44px;
  }
`

type Props = {
  banners: SearchHeroBannerWithText[]
}

export default function SearchHero({ banners }: Props): React.ReactElement | null {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isPaused, setIsPaused] = useState(false)
  const hasMultipleBanners = banners.length > 1

  // Depends on currentIndex so an arrow click also restarts the interval.
  useEffect(() => {
    if (!hasMultipleBanners || isPaused) return
    const timer = setTimeout(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % banners.length)
    }, ROTATION_INTERVAL)
    return () => clearTimeout(timer)
  }, [currentIndex, isPaused, hasMultipleBanners, banners.length])

  if (!banners.length) {
    return null
  }

  const goToNext = (): void => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % banners.length)
  }

  const goToPrev = (): void => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + banners.length) % banners.length)
  }

  return (
    <Root aria-label="באנרים שיווקיים">
      {banners.map((banner, index) => {
        const isActive = index === currentIndex
        const hasText = !!(banner.title || banner.subtitle)
        return (
          <Slide key={index} $active={isActive} aria-hidden={!isActive}>
            <Desktop sx={mediaSx}>
              <Image
                src={banner.desktopImageUrl}
                alt={banner.altText || ""}
                fill
                sizes="100vw"
                priority={index === 0}
                style={{ objectFit: "cover" }}
              />
            </Desktop>
            <Mobile sx={mediaSx}>
              <Image
                src={banner.mobileImageUrl}
                alt={banner.altText || ""}
                fill
                sizes="100vw"
                priority={index === 0}
                style={{ objectFit: "cover" }}
              />
            </Mobile>
            {hasText && (
              <>
                <Scrim />
                <Content>
                  <Title variant="h3" component="p">
                    {banner.title}
                  </Title>
                  <Subtitle variant="body2" component="p">
                    {banner.subtitle}
                  </Subtitle>
                </Content>
              </>
            )}
          </Slide>
        )
      })}
      {hasMultipleBanners && (
        <Controls>
          <ArrowsGroup>
            {/* RTL reading order: the left (←) arrow advances, the right (→) goes back */}
            <ArrowButton aria-label="הבאנר הבא" onClick={goToNext}>
              <Arrow />
            </ArrowButton>
            <ArrowButton aria-label="הבאנר הקודם" onClick={goToPrev}>
              <Arrow flipped />
            </ArrowButton>
          </ArrowsGroup>
          <PauseButton
            aria-label={isPaused ? "הפעלת החלפת באנרים" : "עצירת החלפת באנרים"}
            onClick={() => setIsPaused(!isPaused)}
          >
            <Image
              src={isPaused ? "/assets/playbutton.svg" : "/assets/pausebutton.svg"}
              width={24}
              height={24}
              alt=""
            />
          </PauseButton>
        </Controls>
      )}
    </Root>
  )
}
